/**
 * A badge, as a mark.
 *
 * Each badge names its own glyph in the catalogue and carries its own colour;
 * the tier decides how loud the plate around it is. Mythic gets a gradient
 * ring, the rest get a tinted plate, and a locked badge is drawn in the same
 * shape so the grid does not reflow when you earn one.
 *
 * No title attribute: the browser's own tooltip is slow, unstyled and cannot
 * show the description properly. Tip in HoverCard does that job.
 */

import type { Badge } from '@/lib/types';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';

export function BadgeMark({ badge, size = 26, locked, className }: {
  badge: Badge | undefined;
  size?: number;
  locked?: boolean;
  className?: string;
}) {
  const colour = locked ? 'var(--text-ghost)' : badge?.color ?? 'var(--accent)';
  const mythic = !locked && badge?.tier === 'mythic';

  return (
    <span
      className={cn('relative grid flex-none place-items-center rounded-[30%]', className)}
      style={{
        width: size,
        height: size,
        color: colour,
        background: locked
          ? 'var(--bg-3)'
          : `color-mix(in srgb, ${colour} 15%, transparent)`,
        // Mythic earns a brighter edge; everything else gets a hairline so the
        // plate has a shape even against a similar surface.
        boxShadow: mythic
          ? `inset 0 0 0 1.5px color-mix(in srgb, ${colour} 60%, transparent), 0 0 12px color-mix(in srgb, ${colour} 30%, transparent)`
          : `inset 0 0 0 1px color-mix(in srgb, ${colour} ${locked ? 12 : 30}%, transparent)`
      }}
    >
      <Icon
        name={locked ? 'lock' : badge?.icon ?? 'rosette'}
        size={Math.round(size * 0.62)}
        stroke={size < 24 ? 2 : 1.8}
      />
    </span>
  );
}
